'use client'

import { cn } from '@/lib/utils'
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip'
import { ENGINE_LABELS } from '@/lib/ai/constants'
import type { Engine } from '@/lib/ai/constants'
import type { PromptEngineStatus } from '@/lib/runs/helpers'

const STATUS_CONFIG: Record<
  NonNullable<PromptEngineStatus>,
  { className: string; label: string; filled: boolean }
> = {
  mentioned_cited: {
    className: 'bg-success',
    label: 'Talked about & Linked',
    filled: true,
  },
  mentioned_only: {
    className: 'bg-warning',
    label: 'Talked about',
    filled: true,
  },
  // Filled + own ring (rather than the shared "unfilled" ring) so this reads as
  // its own distinct state, not a variant of mentioned_only or a generic outline.
  cited_not_mentioned: {
    className: 'bg-info ring-1 ring-inset ring-info/40',
    label: 'Linked, not talked about',
    filled: true,
  },
  not_mentioned: {
    className: 'bg-muted-foreground/30',
    label: 'Not talked about',
    filled: false,
  },
  accuracy_issue: {
    className: 'bg-destructive',
    label: 'Accuracy issue',
    filled: true,
  },
  error: {
    className: 'bg-muted-foreground/20',
    label: 'Error',
    filled: false,
  },
  pending: {
    className: 'bg-muted-foreground/20',
    label: 'Scanning…',
    filled: false,
  },
}

/**
 * `status` is typed `PromptEngineStatus` (`… | null`), but it is not always one
 * at runtime: a shared-report snapshot is frozen JSONB, and one taken before an
 * engine existed has NO key for it, so `statuses[engine]` is `undefined`, not
 * `null`. The old `status === null` guard let that through to
 * `STATUS_CONFIG[undefined].label`, which threw and 500'd the whole public
 * `/report/[token]` page — every share created before `google_aio` was added is
 * still serving that error.
 *
 * Looked up defensively rather than narrowed away: the input crosses a
 * persistence boundary the type system does not reach, and an engine we have no
 * record for genuinely means "no data yet".
 */
export function getEngineStatusLabel(status: PromptEngineStatus): string {
  return status ? (STATUS_CONFIG[status]?.label ?? 'No data yet') : 'No data yet'
}

interface EngineStatusDotProps {
  status: PromptEngineStatus
  engine?: Engine
  className?: string
}

export function EngineStatusDot({ status, engine, className }: EngineStatusDotProps) {
  // `!status`, not `status === null` — same frozen-snapshot hazard as
  // `getEngineStatusLabel` above: an engine absent from an old snapshot arrives
  // as `undefined` and would fall through to `STATUS_CONFIG[undefined].className`.
  if (!status || !STATUS_CONFIG[status]) {
    return (
      <span
        className={cn('inline-block size-2.5 rounded-full bg-muted-foreground/15', className)}
        aria-label="No data"
      />
    )
  }

  const config = STATUS_CONFIG[status]
  const engineLabel = engine ? ENGINE_LABELS[engine] : ''
  const tooltipText = engine ? `${engineLabel}: ${config.label}` : config.label

  return (
    <Tooltip>
      <TooltipTrigger
        className={cn(
          'inline-block size-2.5 rounded-full',
          config.className,
          !config.filled && 'ring-1 ring-inset ring-muted-foreground/30',
          className
        )}
        aria-label={tooltipText}
        render={<span />}
      />
      <TooltipContent>{tooltipText}</TooltipContent>
    </Tooltip>
  )
}
